import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Sparkles, Newspaper } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { fireEvent } from '../utils/funnelEvents';

// Canonical index of the hand-written blog posts. Each post lives as a
// standalone page component routed under /blog/* in App.js; this hub is the
// one crawlable index for all of them. Keep this list in sync when a new
// *BlogPage.js ships.
const BLOG_GROUPS = [
  {
    heading: 'Cut the clutter',
    blurb: 'Downloads, screenshots, scans, and badly named PDFs: the folders everyone fights with.',
    posts: [
      {
        slug: 'keep-mac-downloads-folder-clean',
        title: 'How to Keep Your Mac Downloads Folder Clean (5 Strategies That Actually Work)',
        description: 'Manual triage, Stacks, Folder Actions, Hazel, and AI sorting compared, with a setup that keeps the folder clean for good.',
        date: '2026-05-14',
      },
      {
        slug: 'tame-screenshot-chaos-mac',
        title: 'Tame Screenshot Chaos on Mac: Organize, Rename, and Route by Content',
        description: 'Move screenshots off the Desktop, fix the default save location and prefix, then route them into dated folders automatically.',
        date: '2026-05-14',
      },
      {
        slug: 'automatically-rename-pdfs-by-content',
        title: 'Automatically Rename PDFs by Content: Invoices, Receipts, Bank Statements',
        description: 'Read vendor, date, and amount from a PDF and rename it to a clean template like 2026-04-12_Vendor_$142.83.pdf. Working prompt included.',
        date: '2026-05-14',
      },
      {
        slug: 'organize-scanned-documents-by-content',
        title: 'How to Organize Scanned Documents by What Is Inside Them',
        description: 'Scans have no useful name and no metadata. How to file them by what they actually are, and what to do when a scan has no text layer.',
        date: '2026-06-05',
      },
    ],
  },
  {
    heading: 'Teams and shared drives',
    blurb: 'Naming conventions, shared-drive cleanups, and filing systems that survive more than one person.',
    posts: [
      {
        slug: 'clean-up-messy-shared-drive',
        title: 'How to Clean Up a Messy Shared Team Drive',
        description: 'A one-time backlog pass plus a standing rule, so the drive stays clean after the cleanup.',
        date: '2026-06-05',
      },
      {
        slug: 'enforce-file-naming-convention',
        title: 'How to Enforce a File Naming Convention Your Team Actually Follows',
        description: 'Written policies fail because they depend on memory. Enforce the convention at the point of filing instead.',
        date: '2026-05-27',
      },
      {
        slug: 'folder-structure-client-projects',
        title: 'How to Set Up a Consistent Folder Structure for Client Projects',
        description: 'One repeatable per-client, per-project template, plus a working prompt that back-fills the existing mess.',
        date: '2026-06-05',
      },
      {
        slug: 'onboard-employee-shared-drive',
        title: 'How to Onboard a New Employee to a Disorganized Shared Drive',
        description: 'A pre-start cleanup, a one-page where-things-live map, and a shared naming rule so their output matches the team.',
        date: '2026-06-05',
      },
      {
        slug: 'reduce-time-filing-renaming-documents',
        title: 'How to Reduce the Time Staff Spend Filing and Renaming Documents',
        description: 'Move filing and renaming off staff and onto a shared rule that reads each file and applies your template.',
        date: '2026-06-05',
      },
      {
        slug: 'batch-organize-documents-per-client',
        title: 'How to Batch Organize Incoming Documents Per Client Automatically',
        description: 'Point one rule at your intake folder: it reads each document, identifies the client, and files it under that client folder.',
        date: '2026-06-05',
      },
    ],
  },
  {
    heading: 'Client work by industry',
    blurb: 'Filing systems for law firms, accounting firms, consultants, property managers, and real estate teams.',
    posts: [
      {
        slug: 'organize-law-firm-files-by-matter',
        title: 'How to Organize Client Files by Matter at a Law Firm',
        description: 'A matter-centric structure: Client > Matter > Pleadings, Correspondence, Discovery, Contracts, Billing, with a naming pattern to match.',
        date: '2026-06-05',
      },
      {
        slug: 'organize-client-tax-documents-accounting-firm',
        title: 'How to Organize Client Tax Documents for an Accounting Firm',
        description: 'A per-client, per-year folder structure and naming template, so a preparer opens an organized folder instead of a pile.',
        date: '2026-05-27',
      },
      {
        slug: 'make-client-files-audit-ready',
        title: 'How to Make Client Files Audit-Ready (Without the Scramble)',
        description: 'Audit readiness is a side effect of consistent filing. The structure, the template, and the one-pass cleanup.',
        date: '2026-05-27',
      },
      {
        slug: 'organize-consulting-firm-files',
        title: 'How to Organize Files for a Consulting Firm by Client and Engagement',
        description: 'Client, then engagement, then phase: Proposal, Discovery, Working, Deliverables, Final. Folder tree included.',
        date: '2026-06-05',
      },
      {
        slug: 'organize-property-management-documents',
        title: 'How to Organize Property Management Documents by Property and Tenant',
        description: 'Stop losing leases and inspection reports: a Property > Unit/Tenant > category structure with consistent names.',
        date: '2026-06-05',
      },
      {
        slug: 'organize-real-estate-transaction-files',
        title: 'How to Keep Real Estate Transaction Files Organized Across a Team',
        description: 'A per-transaction structure by stage, a naming pattern, and a checklist so every agent files the same way.',
        date: '2026-06-05',
      },
    ],
  },
  {
    heading: 'Choosing your tools',
    blurb: 'Honest comparisons and how to pick between rules, AI, local models, and the cloud.',
    posts: [
      {
        slug: 'best-ai-file-organizers',
        title: 'Best AI File Organizers in 2026',
        description: 'Seven tools compared fairly: Hazel, Renamer, File Juggler, NameQuick, renamed.to, thedrive.ai, and Sortio.',
        date: '2026-06-05',
      },
      {
        slug: 'hazel-content-matching-pdfs',
        title: 'Why Hazel Content Matching Breaks on Real-World PDFs (and What to Do Instead)',
        description: 'Why the regex-on-text approach fails on bank statements and invoices, and what content-aware sorting does differently.',
        date: '2026-05-14',
      },
      {
        slug: 'local-ai-vs-cloud-ai-file-organization',
        title: 'Local AI vs Cloud AI for File Organization: What Leaves Your Mac?',
        description: 'Privacy, speed, accuracy, and cost compared, plus how to switch Sortio to local mode for sensitive folders.',
        date: '2026-05-14',
      },
      {
        slug: 'ai-sort-vs-rule-builder-which-to-use',
        title: 'AI Sort vs Rule Builder in Sortio: Which One Should You Use?',
        description: 'AI Sort runs a model per file; Rule Builder generates deterministic rules. When to pick each one.',
        date: '2026-05-14',
      },
    ],
  },
];

const ALL_POSTS = BLOG_GROUPS.flatMap((g) => g.posts);

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const BlogHubPage = () => {
  const breadcrumbItems = [{ label: 'Blog', href: null }];

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Sortio Blog',
    description:
      'Practical writing on file organization: naming conventions, shared drives, industry filing systems, and how AI sorting actually works.',
    url: 'https://www.getsortio.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
    blogPost: ALL_POSTS.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: `https://www.getsortio.com/blog/${post.slug}`,
      datePublished: post.date,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.getsortio.com/blog' },
    ],
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
      <SEOHead
        title="Sortio Blog: File Organization, Naming Conventions, and AI Sorting"
        description="Practical writing on file organization: cleaning up Downloads and shared drives, naming conventions teams follow, industry filing systems, and honest tool comparisons."
        canonical="https://www.getsortio.com/blog"
        schema={[blogSchema, breadcrumbSchema]}
        keywords="file organization blog, naming conventions, organize downloads folder, shared drive cleanup, ai file sorting"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center p-1.5 rounded-full bg-sortio-secondary/10 px-4 py-1.5">
              <Newspaper className="mr-2 text-sortio-secondary" size={14} />
              <span className="text-xs font-medium uppercase tracking-wider text-sortio-secondary">Blog</span>
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight text-apple-50">
            The Sortio <span className="apple-gradient-text">Blog</span>
          </h1>
          <p className="text-apple-300 text-xl max-w-3xl mx-auto font-light leading-relaxed">
            Practical writing on file organization: messy folders, naming conventions, shared drives, and what AI sorting can and cannot do.
          </p>
          <div className="mt-8 text-apple-400">
            <span className="font-medium">{ALL_POSTS.length}</span> posts
          </div>
        </div>

        {/* Grouped post lists */}
        {BLOG_GROUPS.map((group) => (
          <section key={group.heading} className="mb-16">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-apple-50 mb-2 flex items-center">
                <BookOpen className="text-sortio-primary mr-3" size={22} />
                {group.heading}
              </h2>
              <p className="text-apple-400">{group.blurb}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {group.posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="block p-6 bg-apple-700/30 rounded-2xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 group"
                >
                  <div className="text-xs text-apple-400 mb-2">{formatDate(post.date)}</div>
                  <h3 className="text-lg font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-2 leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-sm text-apple-400 leading-relaxed mb-3">{post.description}</p>
                  <span className="inline-flex items-center text-sm text-sortio-primary">
                    Read post
                    <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {/* Benchmark article lives under /articles for historical reasons */}
        <section className="mb-16">
          <Link
            to="/articles/ai-model-upgrade"
            className="block p-6 bg-apple-700/20 rounded-2xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 group"
          >
            <div className="text-xs text-apple-400 mb-2">{formatDate('2026-03-30')} (in Articles)</div>
            <h3 className="text-lg font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-2">
              Sortio's AI Just Got 5x Faster and Smarter: Model Upgrade Benchmarks
            </h3>
            <p className="text-sm text-apple-400 leading-relaxed">
              Real numbers from the model upgrade: sorting speed, completion rates, and folder quality, measured on the same test sets.
            </p>
          </Link>
        </section>

        {/* More resources */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">More from Sortio</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/guides" className="p-4 bg-apple-700/20 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-colors group">
              <div className="font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-1">Guides</div>
              <p className="text-sm text-apple-400">Step-by-step organization workflows by profession.</p>
            </Link>
            <Link to="/compare" className="p-4 bg-apple-700/20 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-colors group">
              <div className="font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-1">Comparisons</div>
              <p className="text-sm text-apple-400">Sortio vs Hazel, Sparkle, FileJuggler, and more.</p>
            </Link>
            <Link to="/glossary" className="p-4 bg-apple-700/20 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-colors group">
              <div className="font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-1">Glossary</div>
              <p className="text-sm text-apple-400">File organization terms, defined and explained.</p>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-3xl p-8 border border-sortio-primary/20">
            <Sparkles className="mx-auto mb-4 text-sortio-primary" size={32} />
            <h3 className="text-2xl font-semibold text-apple-50 mb-4">
              Stop reading about organized folders. Have one.
            </h3>
            <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
              Sortio is a free download for Mac and Windows. Drop a folder, type a prompt, preview every move before it happens.
            </p>
            <Link
              to="/download"
              onClick={() => fireEvent('cta_click', { cta_id: 'blog_hub_cta', page: '/blog', target: '/download' })}
              className="inline-flex items-center px-8 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogHubPage;
